import React, { useCallback, useLayoutEffect, useState } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { Checkbox, CircularProgress } from '@material-ui/core';
import Popper, { PopperProps } from '@material-ui/core/Popper';
import { IAutocomplete, IOption } from './types';
import useStyles from './style';

const MultiSelect = ({
  id,
  items,
  selectedValues,
  label,
  placeholder,
  selectAllLabel,
  noOptionsText,
  limitTags,
  disableClearable,
  loading,
  hasNextPage,
  onToggleOption,
  onClearOptions,
  onSelectAll,
  getOptionLabel,
  getLimitTagsText,
  fetchNextPage,
  setInputFieldValue,
  selectSearchTerm,
}: IAutocomplete) => {
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const allSelected: boolean = items?.length === selectedValues?.length;
  const isDisabled: boolean = items?.length === 0 && !inputValue && !open;
  const isInterminate: boolean =
    selectedValues?.length > 0 && selectedValues?.length < items?.length;

  useLayoutEffect(() => {
    const divRoot = document.getElementById('root') as HTMLElement;
    const closeOnScroll = () => {
      // TODO: necessita de correção, os componentes de gráfico estão chamando o evento
      // if (open) setOpen(false);
    };
    if (divRoot) divRoot.addEventListener('scroll', closeOnScroll, true);
    return () => {
      if (divRoot) divRoot.removeEventListener('scroll', closeOnScroll);
    };
  }, [open]);

  const itemsValues = React.useMemo(() => {
    return items.map(item => item.value);
  }, [items]);

  const handleToggleSelectAll = useCallback(() => {
    onSelectAll(!allSelected);
  }, [onSelectAll, allSelected]);

  const handleChange = useCallback(
    (
      event: React.ChangeEvent<Record<string, unknown>>,
      selectedOptions: Array<IOption>,
      reason: string,
    ) => {
      if (reason === 'select-option' || reason === 'remove-option') {
        if (selectedOptions.find(option => option.value === 'select-all')) {
          handleToggleSelectAll();
        } else {
          onToggleOption(selectedOptions);
        }
      } else if (reason === 'clear') {
        onClearOptions();
      }
    },
    [handleToggleSelectAll, onToggleOption, onClearOptions],
  );

  const optionRenderer = useCallback(
    (option: IOption, { selected }: { selected: boolean }) => {
      const extraProps: { checked?: boolean; indeterminate?: boolean } = {};
      if (option.value === 'select-all') {
        // To control the state of 'select-all' checkbox
        extraProps.checked = allSelected;
        // To control the state of 'select-all' checkbox
        extraProps.indeterminate = isInterminate;
      }

      return (
        <>
          <Checkbox
            color="primary"
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={selected}
            {...extraProps}
          />
          {getOptionLabel(option)}
        </>
      );
    },
    [allSelected, getOptionLabel, isInterminate],
  );

  const inputRenderer = useCallback(
    (params: TextFieldProps) => (
      <TextField
        variant="outlined"
        {...params}
        label={label}
        placeholder={placeholder}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? (
                <CircularProgress
                  aria-label="autocomplete-loading"
                  color="inherit"
                  size={25}
                />
              ) : null}
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [label, loading, placeholder],
  );

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;
  const filter = createFilterOptions<IOption>();

  const MyPopper = useCallback(
    (popperProps: PopperProps) => (
      <Popper
        {...popperProps}
        style={{ width: 'fit-content' }}
        placement="bottom-start"
      />
    ),
    [],
  );

  React.useEffect(() => {
    if (setInputFieldValue) setInputFieldValue(inputValue);
  }, [inputValue, setInputFieldValue]);

  return (
    <Autocomplete
      multiple
      size="small"
      id={id}
      disabled={isDisabled}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setInputValue('');
        setOpen(false);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input') {
          setInputValue(newInputValue);
        }
      }}
      limitTags={limitTags}
      options={items}
      value={selectedValues}
      PopperComponent={MyPopper}
      disableCloseOnSelect
      disableClearable={disableClearable}
      getOptionDisabled={option => Boolean(option?.disabled)}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      renderTags={value => value.map(option => <span key={option.value} />)}
      getLimitTagsText={getLimitTagsText}
      noOptionsText={noOptionsText}
      onChange={handleChange}
      renderOption={optionRenderer}
      renderInput={inputRenderer}
      className={classes.iconButton}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterOptions={(options, params: any): Array<IOption> => {
        const filtered = filter(options, params);
        if (filtered.length === 0) return filtered;

        if (
          selectSearchTerm &&
          params.inputValue.length >= 2 &&
          !itemsValues.includes(params.inputValue)
        ) {
          filtered.unshift({
            label: params.inputValue,
            value: params.inputValue,
          });
        }

        const newFiltered = [
          { label: selectAllLabel, value: 'select-all' },
          ...filtered,
        ];

        if (loading) {
          return [
            ...newFiltered,
            {
              label: 'Carregando...',
              value: 'carregando',
              disabled: true,
            },
          ];
        }

        return newFiltered;
      }}
      ListboxProps={{
        onScroll: event => {
          const listboxNode = event.currentTarget;

          // Se o número de pixeles desplazados
          // mais a altura do elemento (em px)
          // for maior ou igual ao 98% da altura do elemento
          // e tiver mais págs requisita novamente
          if (
            listboxNode.scrollTop + listboxNode.clientHeight >=
            listboxNode.scrollHeight * 0.98
          ) {
            if (hasNextPage && fetchNextPage && !loading) {
              fetchNextPage();
            }
          }
        },
      }}
    />
  );
};

export default MultiSelect;
